import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserActions } from '@repo/domain-service';
import { TestId } from '@repo/test-id';
import { tr } from '@repo/locale';
import { Button } from '@repo/ui';

import { HANDOFF_TOKEN_QUERY_PARAM } from '@features/auth/constants/handoffRoute';
import { logout } from '@features/auth/services/authService';
import { resolveHandoffDestination } from '@features/auth/utils/resolveHandoffDestination';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

export default function HandoffPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { consumeHandoffToken } = useUserActions();

  const [failed, setFailed] = useState<boolean>(false);
  const consumedRef = useRef<boolean>(false);

  const token = searchParams.get(HANDOFF_TOKEN_QUERY_PARAM) ?? undefined;
  const testId = TestId.root('handoffPage');

  useEffect(() => {
    if (consumedRef.current) return;
    consumedRef.current = true;

    if (!token) {
      setFailed(true);
      return;
    }

    const signIn = async () => {
      // The session handed over always replaces the one already held.
      logout();

      try {
        const { to } = await consumeHandoffToken({ token });
        navigate(resolveHandoffDestination(to, window.location.origin), { replace: true });
      } catch {
        logout();
        setFailed(true);
      }
    };

    void signIn();
  }, [token, consumeHandoffToken, navigate]);

  const handleBack = () => {
    navigate(ROUTE_PATHS[RouteName.MAP], { replace: true });
  };

  if (failed) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center" data-testid={testId}>
        <h1 className="text-heading text-xl font-semibold">{tr('auth:handoff.error.title')}</h1>
        <p className="text-body max-w-md text-sm">{tr('auth:handoff.error.description')}</p>
        <Button
          testId={testId.append('backButton')}
          label={tr('auth:handoff.error.back')}
          color="brand"
          onClick={handleBack}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center p-6 text-center" data-testid={testId}>
      <p className="text-body text-sm">{tr('auth:handoff.loading')}...</p>
    </div>
  );
}
